import { useEffect, useState } from 'react';
import { Link } from 'react-router-dom';
import { PageHeader } from '@/components/layout/PageHeader';
import { TablePagination } from '@/components/ui/TablePagination';
import { useClientPaginatedRows } from '@/hooks/usePagination';
import { deskApi } from '@/api/desk.api';
import { showApiError } from '@/utils/apiError';
import type { DeskAutomationRule } from '@/types/desk';

export function DeskAutomationPage() {
  const [rules, setRules] = useState<DeskAutomationRule[]>([]);
  const { rows: paginatedRules, tablePaginationProps: rulesPaginationProps } = useClientPaginatedRows(rules);

  useEffect(() => {
    deskApi.listAutomationRules().then(setRules).catch((err) => showApiError(err, 'Failed to load automation rules'));
  }, []);

  return (
    <div>
      <PageHeader
        title="Desk Automation"
        description="Rule-based triggers on ticket and SLA events"
        breadcrumbs={[{ label: 'Org', to: '/org' }, { label: 'Desk', to: '/desk' }, { label: 'Automation' }]}
        actions={
          <Link to="/desk/setup" className="btn-primary text-sm">
            Manage in Setup
          </Link>
        }
      />
      <div className="card overflow-hidden">
        <table className="w-full text-sm">
          <thead className="bg-slate-50 dark:bg-slate-800">
            <tr>
              <th className="text-left p-3">Name</th>
              <th className="text-left p-3">Trigger</th>
              <th className="text-left p-3">Active</th>
            </tr>
          </thead>
          <tbody>
            {rules.length === 0 ? (
              <tr>
                <td colSpan={3} className="p-6 text-slate-500">
                  No automation rules yet. Create rules under Desk Setup.
                </td>
              </tr>
            ) : (
              paginatedRules.map((r) => (
                <tr key={r.id} className="border-t border-slate-200 dark:border-slate-700">
                  <td className="p-3">{r.name}</td>
                  <td className="p-3">{r.triggerEvent}</td>
                  <td className="p-3">{r.active ? 'Yes' : 'No'}</td>
                </tr>
              ))
            )}
          </tbody>
        </table>
        <TablePagination {...rulesPaginationProps} hasRows={rules.length > 0} />
      </div>
    </div>
  );
}
